import {
  keepPreviousData,
  useInfiniteQuery,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import {
  getScreens,
  type IScreensReadParams,
  type ScreensReadModel,
} from 'shared/src/api';
import { useFilterListAtom } from 'shared/src/component/AtomicRenderer/ItemList/FilterList/Provider/filterList/useFilterListAtom';
import { filterListStore } from 'shared/src/component/AtomicRenderer/ItemList/FilterList/Provider/filterList/FilterListAtomProvider';
import { filterListStateAtom } from 'shared/src/component/AtomicRenderer/ItemList/FilterList/Provider/filterList/filterList.atom';
import { useGetCommonParams } from '../../hooks';

export const useGetScreensInfiniteQuery = (nextToken?: string) => {
  const { screenName: routeScreenName } = useGetCommonParams();
  const {
    filterListAtomValue: { screen_name, ...restFilterListAtomValue },
  } = useFilterListAtom();

  // 웹에서 이전 화면의 stale atom으로 API 호출 방지
  const isAtomSynced = screen_name === routeScreenName;

  return useInfiniteQuery({
    queryKey: [
      'getScreens',
      { ...restFilterListAtomValue, nextToken, screen_name: routeScreenName },
    ],
    queryFn: ({ pageParam }) => {
      const requestParams = {
        ...restFilterListAtomValue,
        next_token: pageParam ?? null,
        screen: routeScreenName,
      } as IScreensReadParams;
      return getScreens(requestParams);
    },
    enabled: routeScreenName !== undefined && isAtomSynced,
    initialPageParam: nextToken ?? null,
    getNextPageParam: lastPage => {
      return lastPage.nextToken || undefined;
    },
    select: data => {
      const components = data.pages.flatMap(page => page.components);
      const formattedData = {
        ...data.pages[data.pages.length - 1],
        components,
      };

      return formattedData;
    },
    placeholderData: keepPreviousData,
  });
};

export const getScreensInfiniteQueryData = (
  queryClient: QueryClient,
  params: { screenName: string; nextToken?: string; instanceId: string },
): InfiniteData<ScreensReadModel> | undefined => {
  const atomMap = filterListStore.get(filterListStateAtom);
  const atomValue = atomMap[params.instanceId] ?? {};
  const { screen_name, ...restFilterListAtomValue } = atomValue;
  if (screen_name !== params.screenName) return undefined;
  return queryClient.getQueryData<InfiniteData<ScreensReadModel>>([
    'getScreens',
    {
      ...restFilterListAtomValue,
      nextToken: params.nextToken,
      screen_name: params.screenName,
    },
  ]);
};
